
import React, { useState, useEffect } from 'react';
import AddressAutocomplete from '@/components/ui-custom/AddressAutocomplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useLeads, ServiceType } from '@/context/LeadContext';
import { usePartners } from '@/context/PartnerContext';
import { toast } from '@/components/ui/use-toast';
import ThankYouMessage from '@/components/ui-custom/ThankYouMessage';
import { calculateDistance } from '@/utils/geoUtils';

// Import the Partner type from PartnerContext
import type { Partner } from '@/context/PartnerContext';

interface GuestFormProps {
  onSubmitSuccess?: () => void;
}

export default function GuestForm({ onSubmitSuccess }: GuestFormProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [service, setService] = useState<ServiceType>('εξοικονομώ');
  const [timeframe, setTimeframe] = useState('');
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const { addLead } = useLeads();
  const { partners } = usePartners();

  useEffect(() => {
    if (showMessage) {
      const timer = setTimeout(() => {
        setShowMessage(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showMessage]);

  const handleLocationSelect = (lat: number, lng: number, postal: string) => {
    setLatitude(lat);
    setLongitude(lng);
    setPostalCode(postal);
  };

  // Check any partners are within range of the location
  const checkPartnersInRange = (latitude: number, longitude: number, service: ServiceType) => {
    if (!latitude || !longitude) return false;

    const partnersWithLocation = partners.filter(
      p => p.active && p.services.includes(service) && p.latitude && p.longitude && p.radius
    );

    return partnersWithLocation.some(partner => {
      const distance = calculateDistance(latitude, longitude, partner.latitude!, partner.longitude!);
      return distance <= (partner.radius || 10);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !phone || !address || !postalCode || !service || !timeframe) {
      toast({
        title: 'Σφάλμα',
        description: 'Παρακαλώ συμπληρώστε όλα τα υποχρεωτικά πεδία.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const newLead = await addLead(name, phone, address, postalCode, service, timeframe, comments, latitude, longitude);

      if (newLead) {
        setName('');
        setPhone('');
        setAddress('');
        setPostalCode('');
        setService('εξοικονομώ');
        setTimeframe('');
        setComments('');
        setLatitude(null);
        setLongitude(null);
        setShowMessage(true);

        // Call the onSubmitSuccess prop if it exists
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }

        const hasPartnersInRange = checkPartnersInRange(latitude || 0, longitude || 0, service);

        if (!hasPartnersInRange) {
          toast({
            title: 'Επιτυχία',
            description: 'Η φόρμα υποβλήθηκε με επιτυχία!',
          });
        } else {
          toast({
            title: 'Επιτυχία',
            description: 'Η φόρμα υποβλήθηκε με επιτυχία!',
          });
        }
      } else {
        toast({
          title: 'Σφάλμα',
          description: 'Υπήρξε ένα σφάλμα κατα την υποβολή, παρακαλώ προσπαθήστε ξανά.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: 'Σφάλμα',
        description: 'Υπήρξε ένα σφάλμα κατα την υποβολή, παρακαλώ προσπαθήστε ξανά.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {showMessage ? (
        <ThankYouMessage />
      ) : (
        <form onSubmit={handleSubmit} className="max-w-md mx-auto">
          <div className="mb-4">
            <label htmlFor="name" className="block text-gray-700 text-sm font-bold mb-2">
              Όνομα:*
            </label>
            <Input
              type="text"
              id="name"
              placeholder="Το όνομά σας"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="phone" className="block text-gray-700 text-sm font-bold mb-2">
              Τηλέφωνο:*
            </label>
            <Input
              type="tel"
              id="phone"
              placeholder="Το τηλέφωνό σας"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="address" className="block text-gray-700 text-sm font-bold mb-2">
              Διεύθυνση:*
            </label>
            <AddressAutocomplete
              value={address}
              onChange={setAddress}
              onLocationSelect={handleLocationSelect}
              placeholder="Η διεύθυνσή σας"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="postalCode" className="block text-gray-700 text-sm font-bold mb-2">
              ΤΚ:*
            </label>
            <Input
              type="text"
              id="postalCode"
              placeholder="ΤΚ"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="service" className="block text-gray-700 text-sm font-bold mb-2">
              Υπηρεσία:*
            </label>
            <Select value={service} onValueChange={(value: string) => setService(value as ServiceType)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Επιλέξτε υπηρεσία" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="εξοικονομώ">Εξοικονομώ</SelectItem>
                <SelectItem value="αντλίεςΘερμότητας">Αντλίες Θερμότητας</SelectItem>
                <SelectItem value="φωτοβολταϊκά">Φωτοβολταϊκά</SelectItem>
                <SelectItem value="κλιματιστικά">Κλιματιστικά</SelectItem>
                <SelectItem value="φυσικόΑέριο">Φυσικό Αέριο</SelectItem>
                <SelectItem value="ηλιακά">Ηλιακά</SelectItem>
                <SelectItem value="άλλο">Άλλο</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="mb-4">
            <label htmlFor="timeframe" className="block text-gray-700 text-sm font-bold mb-2">
              Χρονικό πλαίσιο:*
            </label>
            <Input
              type="text"
              id="timeframe"
              placeholder="Χρονικό πλαίσιο"
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              required
            />
          </div>
          <div className="mb-6">
            <label htmlFor="comments" className="block text-gray-700 text-sm font-bold mb-2">
              Σχόλια:
            </label>
            <Textarea
              id="comments"
              placeholder="Πρόσθετες πληροφορίες"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={isSubmitting} className="w-full">
            {isSubmitting ? 'Υποβολή...' : 'Υποβολή'}
          </Button>
        </form>
      )}
    </div>
  );
}
